import { useState } from "react";

import { oidcLoginUrl, type MeResponse } from "../api/auth.api.ts";
import { useTokenLogin } from "../lib/auth.ts";

interface Props {
  me: MeResponse;
}

export function Login({ me }: Props) {
  const tokenLogin = useTokenLogin();
  const [token, setToken] = useState("");
  const trimmed = token.trim();
  const valid = trimmed.length >= 32;

  const showSso = me.ssoConfigured;
  const showToken = me.adminTokenLoginEnabled;
  const noPathConfigured = !showSso && !showToken;

  return (
    <div className="flex min-h-screen items-center justify-center bg-zinc-50 px-6">
      <div className="w-full max-w-md rounded-lg border border-zinc-200 bg-white p-6 shadow-sm">
        <img
          src="/oracus-logo.svg"
          alt="Oracus"
          width={116}
          height={30}
          className="mb-4 h-7 w-auto"
        />
        <h1 className="text-xl font-semibold tracking-tight">Sign in to Oracus</h1>

        {noPathConfigured ? (
          <p className="mt-3 text-sm text-zinc-600">
            No sign-in method is configured on this server. Set either{" "}
            <code className="rounded bg-zinc-100 px-1.5 py-0.5 text-xs">ADMIN_TOKEN</code>{" "}
            +{" "}
            <code className="rounded bg-zinc-100 px-1.5 py-0.5 text-xs">SESSION_COOKIE_SECRET</code>{" "}
            for local admin sign-in, or configure OIDC for SSO.
          </p>
        ) : null}

        {showSso ? (
          <a
            href={oidcLoginUrl("/")}
            className="mt-5 inline-flex w-full items-center justify-center rounded-md bg-zinc-900 px-3.5 py-2 text-sm font-medium text-white transition hover:bg-zinc-800"
          >
            Sign in with SSO
          </a>
        ) : null}

        {showSso && showToken ? (
          <div className="my-5 flex items-center gap-3 text-xs uppercase tracking-wide text-zinc-400">
            <div className="h-px flex-1 bg-zinc-200" />
            <span>or</span>
            <div className="h-px flex-1 bg-zinc-200" />
          </div>
        ) : null}

        {showToken ? (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (!valid || tokenLogin.isPending) return;
              tokenLogin.mutate(trimmed);
            }}
            className={`${showSso ? "" : "mt-5"} space-y-3`}
          >
            <label className="block text-sm font-medium text-zinc-800">
              Sign in with admin token
              <input
                type="password"
                autoFocus
                placeholder="Paste your ADMIN_TOKEN"
                className="mt-1 block w-full rounded-md border border-zinc-300 bg-white px-3 py-2 text-sm font-mono shadow-sm placeholder:text-zinc-400 focus:border-zinc-900 focus:outline-none focus:ring-1 focus:ring-zinc-900"
                value={token}
                onChange={(e) => setToken(e.target.value)}
              />
            </label>
            <button
              type="submit"
              disabled={!valid || tokenLogin.isPending}
              className="inline-flex w-full items-center justify-center rounded-md bg-zinc-900 px-3.5 py-2 text-sm font-medium text-white transition hover:bg-zinc-800 disabled:cursor-not-allowed disabled:bg-zinc-300"
            >
              {tokenLogin.isPending ? "Signing in…" : "Sign in"}
            </button>
            {tokenLogin.isError ? (
              <p className="text-xs text-red-600">
                {tokenLogin.error instanceof Error
                  ? tokenLogin.error.message
                  : "Sign-in failed."}
              </p>
            ) : null}
            <p className="text-xs text-zinc-500">
              Paste the value from your server's{" "}
              <code className="rounded bg-zinc-100 px-1 text-xs">ADMIN_TOKEN</code>{" "}
              env var.
            </p>
          </form>
        ) : null}
      </div>
    </div>
  );
}
